import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getEvents, createEvent, updateEvent, deleteEvent, attendEvent } from '../services/api';
import EventCalendar from './EventCalendar';
import QRScanner from './QRScanner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Shiny Button Component - 21st.dev style
const ShinyButton = ({ onClick, disabled, children, variant = 'primary' }) => {
  const baseClasses = variant === 'confirmed' 
    ? 'bg-gradient-to-r from-green-600/80 via-emerald-500/80 to-green-600/80'
    : 'bg-gradient-to-r from-blue-600/80 via-blue-500/80 to-blue-600/80';
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative w-full py-3.5 rounded-2xl font-medium text-sm overflow-hidden group transition-all duration-300 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'
      }`}
    >
      <div className={`absolute inset-0 ${baseClasses}`} />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
      </div>
      <div className={`absolute inset-0 rounded-2xl border ${variant === 'confirmed' ? 'border-green-400/50' : 'border-blue-400/50'}`} />
      <div className={`absolute inset-0 rounded-2xl ${variant === 'confirmed' ? 'shadow-[0_0_20px_rgba(34,197,94,0.3)]' : 'shadow-[0_0_20px_rgba(59,130,246,0.3)]'}`} />
      <span className="relative z-10 text-white font-semibold tracking-wide">{children}</span>
    </button>
  );
};

// Display Cards 3D Component - 21st.dev Prism UI style (exact replica)
const DisplayCards3D = ({ events, currentMember, isAdmin, onAttend, onEdit, onDelete, onViewAttendees, onOpenScanner, onGenerateImage, generatingImage, onInviteVoice }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleCardClick = (index) => {
    if (index === activeIndex) {
      // Click on front card cycles to next
      setActiveIndex((prev) => (prev + 1) % events.length);
    } else {
      setActiveIndex(index);
    }
  };

  if (events.length === 0) {
    return (
      <div className="text-center py-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl backdrop-blur-xl">
        <img src="/w-logo.png" alt="W" className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <p className="text-white/40">Nenhum evento criado ainda</p>
      </div>
    );
  }

  // Reorder events so active is first, then wrap around
  const reorderedEvents = [];
  for (let i = 0; i < Math.min(events.length, 4); i++) {
    const idx = (activeIndex + i) % events.length;
    reorderedEvents.push({ ...events[idx], originalIndex: idx, stackPosition: i });
  }

  return (
    <div 
      className="relative flex items-center justify-start pl-4 py-6 overflow-visible"
      style={{ perspective: '1000px', minHeight: '540px' }}
    >
      {/* 21st.dev style - cards stacked diagonally */}
      <div 
        className="relative ml-2"
        style={{ width: '300px', height: '460px', transformStyle: 'preserve-3d' }}
      >
        {/* Render cards in reverse order so front card renders last (on top) */}
        {[...reorderedEvents].reverse().map((event) => {
          const isAttending = currentMember && event.attendees?.includes(currentMember.id);
          const isFull = event.attendees?.length >= event.max_attendees;
          const pos = event.stackPosition;
          const isActive = pos === 0;
          
          // 21st.dev exact effect - visible diagonal stack
          // pos 0 = front (no offset), pos 1 = behind right-down, pos 2 = further, etc.
          const offsetX = pos * 50; // Move right more
          const offsetY = pos * 35; // Move down more
          const scale = 1 - pos * 0.05;
          const opacity = 1 - pos * 0.2;
          const blur = pos > 0 ? pos * 0.8 : 0;

          return (
            <motion.div
              key={event.id}
              onClick={() => handleCardClick(event.originalIndex)}
              animate={{
                x: offsetX,
                y: offsetY,
                scale: scale,
                opacity: opacity,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute top-0 left-0 w-full cursor-pointer"
              style={{ 
                zIndex: 10 - pos,
                filter: `blur(${blur}px)`,
              }}
              data-testid={`event-card-${event.id}`}
            >
                {/* Card with glass effect */}
                <div 
                  className="rounded-3xl overflow-hidden backdrop-blur-xl"
                  style={{ 
                    background: 'linear-gradient(145deg, rgba(15,20,35,0.98) 0%, rgba(8,12,20,0.99) 100%)',
                    border: isActive ? '1.5px solid rgba(59,130,246,0.5)' : '1px solid rgba(255,255,255,0.1)',
                    boxShadow: isActive 
                      ? '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(59,130,246,0.2)' 
                      : '0 10px 30px rgba(0,0,0,0.3)',
                  }}
                >
                  {/* Animated border glow for active card */}
                  {isActive && (
                    <div className="absolute inset-0 pointer-events-none">
                      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 480" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id={`grad-${event.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                            <stop offset="40%" stopColor="rgba(59,130,246,0.6)" />
                            <stop offset="50%" stopColor="rgba(255,255,255,0.8)" />
                            <stop offset="60%" stopColor="rgba(59,130,246,0.6)" />
                            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                          </linearGradient>
                        </defs>
                        <rect x="1" y="1" width="398" height="478" rx="24" fill="none" stroke={`url(#grad-${event.id})`} strokeWidth="2" strokeDasharray="1800" className="animate-border-spin" />
                      </svg>
                    </div>
                  )}

                  <div className="p-6 space-y-4">
                    {/* Category badge at top */}
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <img src="/w-logo.png" alt="W" className="w-6 h-6" />
                        <span className="text-blue-400 text-xs font-medium uppercase tracking-wider">W Event</span>
                      </div>
                      <span className="px-3 py-1.5 bg-blue-500/15 border border-blue-500/25 text-blue-400 text-xs rounded-full font-medium">
                        {event.price === 0 ? 'Gratis' : `R$ ${event.price}`}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold font-['Jost'] text-white">{event.title}</h3>
                    
                    {/* Description */}
                    <p className="text-white/50 text-sm line-clamp-2">{event.description}</p>
                    
                    {/* Event Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-white/50">
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                        <span>{event.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/50">
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                        </svg>
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/50">
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                        </svg>
                        <span>{event.attendees?.length || 0} / {event.max_attendees}</span>
                      </div>
                    </div>

                    {/* Admin buttons */}
                    {isAdmin && isActive && (
                      <div className="flex gap-2 pt-2 overflow-x-auto pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onViewAttendees(event); }}
                          className="flex-shrink-0 py-2.5 px-3 rounded-2xl text-xs transition-all flex items-center justify-center gap-1.5 backdrop-blur-xl"
                          style={{
                            background: 'linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(139,92,246,0.08) 100%)',
                            border: '1px solid rgba(59,130,246,0.2)',
                            boxShadow: '0 0 15px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
                            color: '#60a5fa'
                          }}
                          data-testid={`view-attendees-btn-${event.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onOpenScanner(event); }}
                          className="flex-shrink-0 py-2.5 px-3 rounded-2xl text-xs transition-all flex items-center justify-center backdrop-blur-xl"
                          style={{
                            background: 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(16,185,129,0.08) 100%)',
                            border: '1px solid rgba(34,197,94,0.2)',
                            boxShadow: '0 0 15px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
                            color: '#4ade80'
                          }}
                          data-testid={`qr-scanner-btn-${event.id}`}
                          title="Check-in QR"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(event); }}
                          className="flex-shrink-0 py-2.5 px-3 rounded-2xl text-xs transition-all backdrop-blur-xl"
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.6)'
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(event.id); }}
                          className="flex-shrink-0 py-2.5 px-3 rounded-2xl text-xs transition-all backdrop-blur-xl"
                          style={{
                            background: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(220,38,38,0.06) 100%)',
                            border: '1px solid rgba(239,68,68,0.2)',
                            color: '#f87171'
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onGenerateImage(event); }}
                          className="flex-shrink-0 py-2.5 px-3 rounded-2xl text-xs transition-all backdrop-blur-xl relative"
                          style={{
                            background: event.promo_image 
                              ? 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(139,92,246,0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(245,158,11,0.08) 100%)',
                            border: event.promo_image 
                              ? '1px solid rgba(168,85,247,0.25)'
                              : '1px solid rgba(251,191,36,0.2)',
                            color: event.promo_image ? '#c084fc' : '#fbbf24'
                          }}
                          title={event.promo_image ? 'Ver/Compartilhar Imagem' : 'Gerar Imagem'}
                          disabled={generatingImage === event.id}
                        >
                          {generatingImage === event.id ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : event.promo_image ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                            </svg>
                          )}
                        </button>
                        {/* W Convite Premium */}
                        <button
                          onClick={(e) => { e.stopPropagation(); onInviteVoice(event); }}
                          className="flex-shrink-0 py-2.5 px-3 rounded-2xl text-xs transition-all backdrop-blur-xl"
                          style={{
                            background: 'linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(6,182,212,0.08) 100%)',
                            border: '1px solid rgba(34,211,238,0.2)',
                            boxShadow: '0 0 15px rgba(34,211,238,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
                            color: '#22d3ee'
                          }}
                          title="W Convite por Voz"
                          data-testid={`invite-voice-btn-${event.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                          </svg>
                        </button>
                      </div>
                    )}

                    {/* Flyer button for members (non-admin) - only show if event has promo image */}
                    {!isAdmin && isActive && event.promo_image && (
                      <div className="pt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); onGenerateImage(event); }}
                          className="w-full py-2.5 rounded-2xl text-xs transition-all flex items-center justify-center gap-2 backdrop-blur-xl"
                          style={{
                            background: 'linear-gradient(135deg, rgba(168,85,247,0.12) 0%, rgba(139,92,246,0.08) 100%)',
                            border: '1px solid rgba(168,85,247,0.2)',
                            boxShadow: '0 0 15px rgba(168,85,247,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
                            color: '#c084fc'
                          }}
                          data-testid={`view-flyer-btn-${event.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                          </svg>
                          Ver Flyer
                        </button>
                      </div>
                    )}

                    {/* Attend button */}
                    {isActive && (
                      <div className="pt-2">
                        {isAttending ? (
                          <ShinyButton variant="confirmed" disabled>
                            <span className="flex items-center justify-center gap-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Confirmado | Pago
                            </span>
                          </ShinyButton>
                        ) : (
                          <ShinyButton
                            onClick={(e) => { e.stopPropagation(); onAttend(event); }}
                            disabled={isFull}
                            variant="primary"
                          >
                            {isFull ? 'Evento Lotado' : 'Confirmar Presenca'}
                          </ShinyButton>
                        )}
                      </div>
                    )}
                  </div>
                </div>
            </motion.div>
          );
        })}
      </div>

      {/* Navigation hint */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/20 text-xs font-['Jost']">
        Toque para navegar • {activeIndex + 1} de {events.length}
      </div>

      {/* Global animation styles */}
      <style>{`
        @keyframes borderSpin {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -3600; }
        }
        .animate-border-spin {
          animation: borderSpin 6s linear infinite;
        }
      `}</style>
    </div>
  );
};

const EventsSection = ({ currentMember, isAdmin }) => {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('3d'); // '3d' | 'grid' | 'calendar'
  const [notif, setNotif] = useState(null);
  const [confirmAttend, setConfirmAttend] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('wcash');
  const [wcashBalance, setWcashBalance] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [attendeesModal, setAttendeesModal] = useState(null);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [attendeesFilter, setAttendeesFilter] = useState('all'); // 'all' | 'pago' | 'pendente'
  const [scannerModal, setScannerModal] = useState(null); // { eventId, eventTitle }
  const [checkInStats, setCheckInStats] = useState(null); // { total, checked }
  const [generatingImage, setGeneratingImage] = useState(null); // event_id being generated
  const [promoImageModal, setPromoImageModal] = useState(null); // { eventId, imageBase64, title, event }
  const [customPrompt, setCustomPrompt] = useState('');
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [showOverlayEditor, setShowOverlayEditor] = useState(false);
  const [overlayData, setOverlayData] = useState({ title: '', date: '', location: '', price: '', vagas: '' });
  const [uploadedPhoto, setUploadedPhoto] = useState(null); // base64 of uploaded photo
  const [shareFormat, setShareFormat] = useState(null); // 'feed' | 'stories' - shows format picker
  const [logoSettings, setLogoSettings] = useState({ position: 'top-left', size: 150, customLogo: null }); // Logo config - min 150, max 300
  const [showLogoEditor, setShowLogoEditor] = useState(false);
  const [inviteModal, setInviteModal] = useState(null); // event object for voice invite
  const [inviteMembers, setInviteMembers] = useState([]);
  const [inviteSending, setInviteSending] = useState(null); // member_id being sent
  const [inviteSent, setInviteSent] = useState([]); // member_ids already sent // Show logo editor panel
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    address: '',
    price: 0,
    max_attendees: 0,
  });

  const showNotifMsg = (msg, type = 'info') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3000);
  };

  const handleOpenInvite = async (event) => {
    setInviteModal(event);
    setInviteSent([]);
    try {
      const r = await axios.get(`${API}/members`);
      setInviteMembers(r.data || []);
    } catch (e) { console.error(e); }
  };

  const handleSendInvite = async (member) => {
    if (!inviteModal) return;
    setInviteSending(member.id);
    try {
      const r = await axios.post(`${API}/events/${inviteModal.id}/invite-voice`, { member_id: member.id });
      if (r.data.success) {
        setInviteSent(prev => [...prev, member.id]);
        // Open WhatsApp link if available
        if (r.data.wa_link) {
          window.open(r.data.wa_link, '_blank');
        }
        showNotifMsg(`Convite enviado para ${member.social_name || member.name}!`, 'success');
      }
    } catch (e) {
      showNotifMsg('Erro ao enviar convite', 'error');
    }
    setInviteSending(null);
  };

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (currentMember) {
      loadWcashBalance();
    }
  }, [currentMember]);

  const loadWcashBalance = async () => {
    if (!currentMember) return;
    try {
      const res = await axios.get(`${API}/wallet/${currentMember.id}/wcash`);
      setWcashBalance(res.data?.wcash_balance || 0);
    } catch (e) {
      setWcashBalance(0);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await getEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, formData);
        showNotifMsg('Evento atualizado!', 'success');
      } else {
        await createEvent(formData);
        showNotifMsg('Evento criado!', 'success');
      }
      await loadEvents();
      closeModal();
    } catch (error) {
      showNotifMsg(error.response?.data?.detail || 'Erro ao salvar evento', 'warn');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Tem certeza que deseja deletar este evento?')) return;
    try {
      await deleteEvent(eventId);
      await loadEvents();
      showNotifMsg('Evento deletado!', 'success');
    } catch (error) {
      showNotifMsg(error.response?.data?.detail || 'Erro ao deletar', 'warn');
    }
  };

  // Format date helper
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  // Handle photo upload from gallery
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setUploadedPhoto(base64);
      showNotifMsg('Foto carregada!', 'success');
    };
    reader.readAsDataURL(file);
  };

  // Use uploaded photo as promo image
  const useUploadedPhoto = () => {
    if (!uploadedPhoto || !promoImageModal) return;
    
    const event = promoImageModal.event;
    const updatedEvent = { ...event, promo_image: uploadedPhoto };
    setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
    setPromoImageModal({ ...promoImageModal, imageBase64: uploadedPhoto, event: updatedEvent });
    
    // Save to database
    const token = localStorage.getItem('wclub_admin_token');
    axios.put(`${API}/events/${event.id}/promo-image`, { promo_image: uploadedPhoto }, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch((err) => {
      console.error('Erro ao salvar imagem:', err);
      showNotifMsg('Erro ao salvar imagem no servidor', 'warn');
    });
    
    setUploadedPhoto(null);
    showNotifMsg('Foto aplicada!', 'success');
  };

  // Initialize overlay data when modal opens
  const openPromoModal = (event, imageBase64) => {
    setOverlayData({
      title: event.title || '',
      date: formatEventDate(event.date),
      location: event.location || '',
      price: event.price > 0 ? `R$ ${event.price.toFixed(2).replace('.', ',')}` : 'FREE',
      vagas: event.max_attendees > 0 ? `${event.max_attendees} vagas` : ''
    });
    setPromoImageModal({ eventId: event.id, imageBase64, title: event.title, event });
  };

  const handleGenerateImage = async (event, promptOverride = null) => {
    if (generatingImage) return;
    
    // If event already has image and no override prompt, show it
    if (event.promo_image && !promptOverride) {
      openPromoModal(event, event.promo_image);
      return;
    }
    
    setGeneratingImage(event.id);
    showNotifMsg('Gerando imagem promocional...', 'info');
    
    try {
      const token = localStorage.getItem('wclub_admin_token');
      const res = await axios.post(`${API}/events/${event.id}/generate-image`, 
        { custom_prompt: promptOverride || null },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.success) {
        // Update event in state
        const updatedEvent = { ...event, promo_image: res.data.image_base64 };
        setEvents(prev => prev.map(e => 
          e.id === event.id ? updatedEvent : e
        ));
        openPromoModal(updatedEvent, res.data.image_base64);
        setCustomPrompt('');
        setShowPromptEditor(false);
        showNotifMsg('Imagem gerada com sucesso!', 'success');
      }
    } catch (error) {
      showNotifMsg(error.response?.data?.detail || 'Erro ao gerar imagem', 'warn');
    } finally {
      setGeneratingImage(null);
    }
  };

  // Open format picker before sharing
  const openShareFormatPicker = () => {
    setShareFormat('picker'); // Show format picker modal
  };

  // Helper function to load custom logo
  const loadCustomLogo = (base64) => {
    return new Promise((resolve) => {
      const customImg = new Image();
      customImg.crossOrigin = 'anonymous';
      customImg.onload = () => resolve(customImg);
      customImg.onerror = () => resolve(null);
      customImg.src = `data:image/png;base64,${base64}`;
    });
  };

  // Handle custom logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setLogoSettings(prev => ({ ...prev, customLogo: base64 }));
      showNotifMsg('Logo carregado!', 'success');
    };
    reader.readAsDataURL(file);
  };

  const handleShareImage = async (imageBase64, title, format = 'feed') => {
    try {
      showNotifMsg('Preparando imagem...', 'info');
      setShareFormat(null); // Close picker
      
      // Create canvas to render image with overlay
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Polyfill for roundRect if not available
      if (!ctx.roundRect) {
        ctx.roundRect = function(x, y, w, h, r) {
          if (w < 2 * r) r = w / 2;
          if (h < 2 * r) r = h / 2;
          this.beginPath();
          this.moveTo(x + r, y);
          this.arcTo(x + w, y, x + w, y + h, r);
          this.arcTo(x + w, y + h, x, y + h, r);
          this.arcTo(x, y + h, x, y, r);
          this.arcTo(x, y, x + w, y, r);
          this.closePath();
          return this;
        };
      }
      
      // Load the base image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = `data:image/png;base64,${imageBase64}`;
      });
      
      // Load the logo image
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = () => resolve(); // Continue even if logo fails
        logo.src = '/w-logo-chrome.png';
      });
      
      // Calculate dimensions based on format
      let canvasWidth, canvasHeight, srcX, srcY, srcW, srcH;
      
      if (format === 'stories') {
        // Stories: 9:16 vertical (1080x1920)
        canvasWidth = 1080;
        canvasHeight = 1920;
        
        // Crop from center of original image to fit 9:16
        const targetRatio = 9 / 16;
        const imgRatio = img.width / img.height;
        
        if (imgRatio > targetRatio) {
          // Image is wider - crop sides
          srcH = img.height;
          srcW = img.height * targetRatio;
          srcX = (img.width - srcW) / 2;
          srcY = 0;
        } else {
          // Image is taller - crop top/bottom
          srcW = img.width;
          srcH = img.width / targetRatio;
          srcX = 0;
          srcY = (img.height - srcH) / 2;
        }
      } else {
        // Feed: 4:5 vertical (1080x1350) - Instagram recommended
        canvasWidth = 1080;
        canvasHeight = 1350;
        
        const targetRatio = 4 / 5;
        const imgRatio = img.width / img.height;
        
        if (imgRatio > targetRatio) {
          // Image is wider - crop sides
          srcH = img.height;
          srcW = img.height * targetRatio;
          srcX = (img.width - srcW) / 2;
          srcY = 0;
        } else {
          // Image is taller - crop top/bottom
          srcW = img.width;
          srcH = img.width / targetRatio;
          srcX = 0;
          srcY = (img.height - srcH) / 2;
        }
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      // Draw cropped/scaled base image
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, canvasWidth, canvasHeight);
      
      // Calculate sizes relative to canvas
      const scale = canvasWidth / 1080;
      const padding = Math.round(50 * scale);
      const logoSizeBase = Math.round(logoSettings.size * scale); // Use settings (150-300)
      const titleSize = format === 'stories' ? Math.round(52 * scale) : Math.round(44 * scale);
      const descSize = Math.round(22 * scale);
      const detailSize = Math.round(24 * scale);
      const priceSize = Math.round(36 * scale);
      
      // Draw top gradient overlay (for logo and price)
      const topGradient = ctx.createLinearGradient(0, 0, 0, canvasHeight * 0.2);
      topGradient.addColorStop(0, 'rgba(0,0,0,0.85)');
      topGradient.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = topGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight * 0.2);
      
      // Draw bottom gradient overlay (for event info)
      const bottomGradient = ctx.createLinearGradient(0, canvasHeight * 0.55, 0, canvasHeight);
      bottomGradient.addColorStop(0, 'rgba(0,0,0,0)');
      bottomGradient.addColorStop(0.3, 'rgba(0,0,0,0.5)');
      bottomGradient.addColorStop(1, 'rgba(0,0,0,0.95)');
      ctx.fillStyle = bottomGradient;
      ctx.fillRect(0, canvasHeight * 0.55, canvasWidth, canvasHeight * 0.45);
      
      // ─── LOGO: Top-left with vertical adjustment ───
      const logoX = padding;
      const logoY = Math.round((logoSettings.verticalOffset || 50) * scale);
      const logoActualSize = logoSizeBase;
      
      // Draw logo (custom or default)
      const logoToDraw = logoSettings.customLogo ? await loadCustomLogo(logoSettings.customLogo) : logo;
      if (logoToDraw && logoToDraw.complete && logoToDraw.naturalWidth > 0) {
        ctx.shadowColor = 'rgba(0,0,0,0.8)';
        ctx.shadowBlur = 20;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;
        ctx.drawImage(logoToDraw, logoX, logoY, logoActualSize, logoActualSize);
      }
      
      // Draw "WCLUB" text below logo
      ctx.font = `bold ${Math.round(28 * scale)}px 'Jost', sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 15;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      const wclubText = 'WCLUB';
      const wclubTextWidth = ctx.measureText(wclubText).width;
      // Center text below logo
      const wclubX = logoX + (logoActualSize - wclubTextWidth) / 2;
      const wclubY = logoY + logoActualSize + Math.round(30 * scale);
      ctx.fillText(wclubText, wclubX, wclubY);
      
      // Get overlay data
      const eventTitle = overlayData.title || title;
      const eventDesc = promoImageModal?.event?.description || '';
      const eventDate = overlayData.date || '';
      const eventLocation = overlayData.location || '';
      const eventPrice = overlayData.price || 'FREE';
      
      // ─── PRICE: Top-right corner with white outline ───
      ctx.font = `bold ${priceSize}px 'Jost', sans-serif`;
      const priceText = eventPrice;
      const pricePadding = Math.round(20 * scale);
      const priceX = canvasWidth - padding - ctx.measureText(priceText).width - pricePadding * 2;
      const priceY = padding + Math.round(50 * scale);
      
      // Price badge with white outline only (transparent fill)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.round(3 * scale);
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 15;
      
      const priceBoxWidth = ctx.measureText(priceText).width + pricePadding * 2;
      const priceBoxHeight = Math.round(55 * scale);
      const priceBoxRadius = Math.round(12 * scale);
      
      // Draw rounded rectangle outline
      ctx.beginPath();
      ctx.roundRect(priceX, priceY - priceBoxHeight + Math.round(15 * scale), priceBoxWidth, priceBoxHeight, priceBoxRadius);
      ctx.stroke();
      
      // Price text in white
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 10;
      ctx.fillText(priceText, priceX + pricePadding, priceY);
      
      // ─── EVENT INFO: Bottom aligned (1-Name, 2-Desc, 3-Date, 4-Address) ───
      ctx.shadowColor = 'rgba(0,0,0,0.9)';
      ctx.shadowBlur = 20;
      
      // Calculate vertical positions from bottom
      const bottomBase = canvasHeight - padding;
      const lineSpacing = Math.round(35 * scale);
      
      // 4. Address (bottom)
      if (eventLocation) {
        ctx.font = `${detailSize}px 'Jost', sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        const addrY = bottomBase - Math.round(10 * scale);
        ctx.fillText(eventLocation, padding, addrY);
      }
      
      // 3. Date and time
      if (eventDate) {
        ctx.font = `${detailSize}px 'Jost', sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        const dateY = bottomBase - lineSpacing - Math.round(10 * scale);
        ctx.fillText(eventDate, padding, dateY);
      }
      
      // 2. Description (truncated if too long)
      if (eventDesc) {
        ctx.font = `${descSize}px 'Jost', sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        const maxDescWidth = canvasWidth - padding * 2;
        let descText = eventDesc;
        if (ctx.measureText(descText).width > maxDescWidth) {
          while (ctx.measureText(descText + '...').width > maxDescWidth && descText.length > 0) {
            descText = descText.slice(0, -1);
          }
          descText += '...';
        }
        const descY = bottomBase - lineSpacing * 2 - Math.round(10 * scale);
        ctx.fillText(descText, padding, descY);
      }
      
      // 1. Event title (biggest, at top of info section)
      ctx.font = `bold ${titleSize}px 'Jost', sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 25;
      
      // Word wrap title if too long
      const maxTitleWidth = canvasWidth - padding * 2;
      const titleLines = [];
      const words = eventTitle.toUpperCase().split(' ');
      let currentLine = '';
      
      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(testLine).width > maxTitleWidth) {
          if (currentLine) titleLines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) titleLines.push(currentLine);
      
      const titleLineHeight = titleSize * 1.15;
      const titleStartY = bottomBase - lineSpacing * 3 - Math.round(15 * scale) - (titleLines.length - 1) * titleLineHeight;
      
      titleLines.forEach((line, index) => {
        ctx.fillText(line, padding, titleStartY + index * titleLineHeight);
      });
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.95));
      const fileName = `wclub-${title.replace(/\s+/g, '-')}-${format}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `WClub - ${title}`,
          text: `Evento exclusivo WClub: ${title}`
        });
      } else {
        // Fallback: download image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        showNotifMsg('Imagem salva!', 'success');
      }
    } catch (error) {
      console.error('Share error:', error);
      showNotifMsg('Erro ao compartilhar', 'warn');
    }
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      location: event.location,
      address: event.address || '',
      price: event.price,
      max_attendees: event.max_attendees,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setFormData({ title: '', description: '', date: '', location: '', address: '', price: 0, max_attendees: 0 });
  };

  const handleAttendClick = (event) => {
    if (!currentMember) {
      showNotifMsg('Selecione um membro na aba Cartoes', 'warn');
      return;
    }
    loadWcashBalance();
    setConfirmAttend({ eventId: event.id, eventTitle: event.title, eventPrice: event.price });
    setPaymentMethod(event.price === 0 ? 'free' : 'wcash');
  };

  const confirmAttendance = async () => {
    if (!confirmAttend) return;
    setProcessingPayment(true);
    
    try {
      if (confirmAttend.eventPrice > 0 && paymentMethod === 'wcash') {
        if (wcashBalance < confirmAttend.eventPrice) {
          showNotifMsg('Saldo Wcash insuficiente', 'warn');
          setProcessingPayment(false);
          return;
        }
        
        await axios.post(`${API}/wallet/${currentMember.id}/wcash/use`, {
          amount: confirmAttend.eventPrice,
          description: `Evento: ${confirmAttend.eventTitle}`
        });
        
        await axios.post(`${API}/payments`, {
          member_id: currentMember.id,
          member_name: currentMember.name,
          type: 'evento',
          amount: confirmAttend.eventPrice,
          description: `Evento: ${confirmAttend.eventTitle}`,
          payment_method: 'wcash',
          status: 'pago',
          paid_at: new Date().toISOString()
        });
      }
      
      await attendEvent({ event_id: confirmAttend.eventId, member_id: currentMember.id });
      await loadEvents();
      await loadWcashBalance();
      
      if (paymentMethod === 'wcash' && confirmAttend.eventPrice > 0) {
        showNotifMsg('Presenca confirmada e pago com Wcash!', 'success');
      } else if (paymentMethod === 'contact') {
        showNotifMsg('Presenca confirmada! Entre em contato com o WClub.', 'success');
      } else {
        showNotifMsg('Presenca confirmada!', 'success');
      }
    } catch (error) {
      showNotifMsg(error.response?.data?.detail || 'Erro ao confirmar presenca', 'warn');
    }
    
    setProcessingPayment(false);
    setConfirmAttend(null);
  };

  // Admin: carregar participantes de um evento
  const loadEventAttendees = async (event) => {
    if (!isAdmin) return;
    setAttendeesLoading(true);
    setAttendeesFilter('all'); // Reset filter when opening modal
    try {
      const token = localStorage.getItem('wclub_admin_token');
      const res = await axios.get(`${API}/events/${event.id}/attendees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendeesModal({
        eventId: event.id,
        eventTitle: res.data.event_title,
        totalAttendees: res.data.total_attendees,
        attendees: res.data.attendees,
        maxAttendees: event.max_attendees,
        price: event.price,
        checkinKey: event.checkin_key // Include the check-in key
      });
    } catch (error) {
      showNotifMsg('Erro ao carregar participantes', 'warn');
    }
    setAttendeesLoading(false);
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'pago':
        return { text: 'Pago', bg: 'bg-green-500/15', border: 'border-green-500/30', color: 'text-green-400' };
      case 'pendente':
        return { text: 'Pendente', bg: 'bg-amber-500/15', border: 'border-amber-500/30', color: 'text-amber-400' };
      case 'gratis':
        return { text: 'Gratis', bg: 'bg-blue-500/15', border: 'border-blue-500/30', color: 'text-blue-400' };
      default:
        return { text: 'Pendente', bg: 'bg-amber-500/15', border: 'border-amber-500/30', color: 'text-amber-400' };
    }
  };

  const getCategoryBadge = (cat) => {
    const cats = {
      gold: { text: 'Gold', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
      black: { text: 'Black', color: 'text-slate-300', bg: 'bg-slate-500/10' },
      diamond: { text: 'Diamond', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
      vip: { text: 'VIP', color: 'text-purple-400', bg: 'bg-purple-500/10' },
      daypass: { text: 'Day Pass', color: 'text-orange-400', bg: 'bg-orange-500/10' }
    };
    return cats[cat] || { text: cat, color: 'text-white/50', bg: 'bg-white/5' };
  };

  // Open QR Scanner for check-in
  const openScanner = async (event) => {
    setScannerModal({
      eventId: event.id,
      eventTitle: event.title
    });
    // Load current check-in stats
    await loadCheckInStats(event.id);
  };

  const loadCheckInStats = async (eventId) => {
    try {
      const token = localStorage.getItem('wclub_admin_token');
      const res = await axios.get(`${API}/events/${eventId}/checkins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckInStats({
        total: res.data.total_attendees,
        checked: res.data.total_checkins
      });
    } catch (err) {
      console.error('Error loading check-in stats:', err);
    }
  };

  // Handle QR scan check-in
  const handleCheckIn = async (memberId) => {
    if (!scannerModal) return;
    
    try {
      const token = localStorage.getItem('wclub_admin_token');
      const res = await axios.post(
        `${API}/events/${scannerModal.eventId}/checkin`,
        { member_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update stats
      await loadCheckInStats(scannerModal.eventId);
      
      return {
        success: true,
        message: res.data.message,
        member: res.data.member
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.detail || 'Erro ao fazer check-in'
      };
    }
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notif && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`px-4 py-2.5 rounded-xl text-xs font-['Jost'] ${
              notif.type === 'success' ? 'bg-green-500/10 border border-green-500/15 text-green-400/80' :
              notif.type === 'warn' ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400/80' :
              'bg-white/5 border border-white/8 text-white/50'
            }`}
          >{notif.msg}</motion.div>
        )}
      </AnimatePresence>
      
      {/* Header */}
      <div className="relative rounded-3xl overflow-hidden p-6 backdrop-blur-xl border border-white/[0.08]"
           style={{ background: 'linear-gradient(145deg, rgba(10,15,30,0.95) 0%, rgba(5,10,20,0.98) 100%)' }}>
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
          <defs>
            <linearGradient id="eventHeaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0)" />
              <stop offset="40%" stopColor="rgba(59,130,246,0.4)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="60%" stopColor="rgba(148,163,184,0.4)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" rx="24" fill="none" stroke="url(#eventHeaderGrad)" strokeWidth="1" strokeDasharray="1200" className="animate-header-border" />
        </svg>
        <style>{`
          @keyframes headerBorder { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -2400; } }
          .animate-header-border { animation: headerBorder 10s linear infinite; }
        `}</style>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
          <div className="flex items-center gap-4">
            <img src="/w-logo.png" alt="W" className="w-12 h-12 object-contain" />
            <div>
              <h2 className="text-2xl font-bold font-['Jost'] text-white/90">W Event</h2>
              <p className="text-white/40 text-sm">Experiencias premium do WClub</p>
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-4 py-2 bg-white/[0.05] border border-white/[0.1] rounded-xl text-sm text-white/60 outline-none"
            >
              <option value="3d">3D Cards</option>
              <option value="grid">Grid</option>
              <option value="calendar">Calendario</option>
            </select>
            {isAdmin && (
              <button
                onClick={() => setShowModal(true)}
                className="px-5 py-2 bg-blue-500/20 border border-blue-500/30 rounded-xl text-sm text-blue-400 hover:bg-blue-500/30 transition font-medium"
              >
                + Criar Evento
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Events Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64"><div className="spinner"></div></div>
      ) : viewMode === '3d' ? (
        <DisplayCards3D 
          events={events} 
          currentMember={currentMember}
          isAdmin={isAdmin}
          onAttend={handleAttendClick}
          onEdit={openEditModal}
          onDelete={handleDelete}
          onViewAttendees={loadEventAttendees}
          onOpenScanner={openScanner}
          onGenerateImage={handleGenerateImage}
          generatingImage={generatingImage}
          onInviteVoice={handleOpenInvite}
        />
      ) : viewMode === 'calendar' ? (
        <EventCalendar events={events} />
      ) : (
        // Grid View (simplified)
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => {
            const isAttending = currentMember && event.attendees?.includes(currentMember.id);
            const isFull = event.attendees?.length >= event.max_attendees;
            return (
              <div key={event.id} className="rounded-2xl p-5 backdrop-blur-xl border border-white/[0.08]"
                   style={{ background: 'linear-gradient(145deg, rgba(15,20,35,0.9) 0%, rgba(10,15,25,0.95) 100%)' }}>
                <h3 className="text-lg font-bold text-white mb-2">{event.title}</h3>
                <p className="text-white/50 text-sm mb-3">{event.description}</p>
                <p className="text-white/40 text-xs mb-3">{event.date} • {event.location}</p>
                <ShinyButton onClick={() => handleAttendClick(event)} disabled={isAttending || isFull} variant={isAttending ? 'confirmed' : 'primary'}>
                  {isAttending ? 'Confirmado | Pago' : isFull ? 'Lotado' : 'Confirmar'}
                </ShinyButton>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="rounded-2xl p-6 max-w-md w-full border border-white/[0.1] backdrop-blur-xl"
              style={{ background: 'linear-gradient(145deg, rgba(15,20,35,0.98) 0%, rgba(10,15,25,0.99) 100%)' }}>
              <div className="flex items-center gap-3 mb-6">
                <img src="/w-logo.png" alt="W" className="w-8 h-8" />
                <h2 className="text-xl font-bold font-['Jost'] text-white/90">{editingEvent ? 'Editar Evento' : 'Criar Evento'}</h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Titulo" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none" />
                <textarea placeholder="Descricao" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none resize-none" />
                <input type="text" placeholder="Data (ex: 15/06/2025 - 19h)" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none" />
                <input type="text" placeholder="Local" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm">R$</span>
                    <input 
                      type="text" 
                      inputMode="decimal"
                      placeholder="0,00" 
                      value={formData.price === 0 ? '' : formData.price.toString().replace('.', ',')} 
                      onChange={(e) => {
                        const val = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
                        setFormData({ ...formData, price: val === '' ? 0 : parseFloat(val) || 0 });
                      }}
                      onFocus={(e) => { if (formData.price === 0) e.target.value = ''; }}
                      className="w-full pl-10 pr-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none" 
                    />
                    {formData.price === 0 && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-400/60 text-xs">Free</span>
                    )}
                  </div>
                  <div className="relative">
                    <input 
                      type="text" 
                      inputMode="numeric"
                      placeholder="Vagas" 
                      value={formData.max_attendees === 0 ? '' : formData.max_attendees} 
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, max_attendees: val === '' ? 0 : parseInt(val) || 0 });
                      }}
                      onFocus={(e) => { if (formData.max_attendees === 20 || formData.max_attendees === 0) e.target.value = ''; setFormData({ ...formData, max_attendees: 0 }); }}
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none" 
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 text-xs">vagas</span>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button type="submit" className="flex-1 px-6 py-3 bg-blue-500/20 border border-blue-500/30 text-blue-400 font-semibold rounded-xl hover:bg-blue-500/30 transition">{editingEvent ? 'Salvar' : 'Criar'}</button>
                  <button type="button" onClick={closeModal} className="px-6 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white/50 hover:bg-white/[0.08] transition">Cancelar</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Attendance Modal */}
      <AnimatePresence>
        {confirmAttend && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setConfirmAttend(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="rounded-2xl p-6 max-w-sm w-full border border-white/[0.1] backdrop-blur-xl"
              style={{ background: 'linear-gradient(145deg, rgba(15,20,35,0.98) 0%, rgba(10,15,25,0.99) 100%)' }}>
              <div className="text-center space-y-4">
                <img src="/w-logo.png" alt="W" className="w-14 h-14 mx-auto" />
                <h3 className="text-lg font-['Jost'] font-semibold text-white/90">Confirmar Presenca</h3>
                <p className="text-sm text-white/50">Evento: <span className="text-white/70">"{confirmAttend.eventTitle}"</span></p>
                
                {confirmAttend.eventPrice > 0 ? (
                  <>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                      <p className="text-blue-400 text-lg font-bold">R$ {confirmAttend.eventPrice}</p>
                    </div>
                    
                    <div className="space-y-2 text-left">
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">Forma de Pagamento</p>
                      <button onClick={() => setPaymentMethod('wcash')} className={`w-full p-3 rounded-xl border transition flex items-center gap-3 ${paymentMethod === 'wcash' ? 'bg-blue-500/15 border-blue-500/30' : 'bg-white/[0.03] border-white/[0.08]'}`}>
                        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center"><span className="text-blue-400 font-bold text-xs">WC$</span></div>
                        <div className="flex-1 text-left"><p className="text-white/80 text-sm font-medium">Wcash</p><p className="text-white/40 text-xs">Saldo: R$ {wcashBalance.toFixed(2)}</p></div>
                      </button>
                      <button onClick={() => setPaymentMethod('contact')} className={`w-full p-3 rounded-xl border transition flex items-center gap-3 ${paymentMethod === 'contact' ? 'bg-slate-500/15 border-slate-500/30' : 'bg-white/[0.03] border-white/[0.08]'}`}>
                        <div className="w-10 h-10 bg-slate-500/20 rounded-lg flex items-center justify-center"><img src="/w-logo.png" alt="W" className="w-6 h-6" /></div>
                        <div className="flex-1 text-left"><p className="text-white/80 text-sm font-medium">W Pay</p><p className="text-white/40 text-xs">PIX, Credito ou Debito</p></div>
                      </button>
                    </div>
                    
                    {paymentMethod === 'wcash' && wcashBalance < confirmAttend.eventPrice && (
                      <p className="text-xs text-red-400 bg-red-500/10 p-2 rounded-lg">Saldo insuficiente</p>
                    )}
                  </>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                    <p className="text-green-400 font-medium">Evento Gratuito</p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setConfirmAttend(null)} className="flex-1 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white/50">Voltar</button>
                  <button onClick={confirmAttendance} disabled={processingPayment || (paymentMethod === 'wcash' && wcashBalance < confirmAttend.eventPrice && confirmAttend.eventPrice > 0)}
                    className={`flex-1 py-3 rounded-xl font-semibold text-sm transition ${processingPayment || (paymentMethod === 'wcash' && wcashBalance < confirmAttend.eventPrice && confirmAttend.eventPrice > 0) ? 'bg-white/[0.05] text-white/30' : 'bg-blue-500/20 border border-blue-500/30 text-blue-400'}`}>
                    {processingPayment ? 'Processando...' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Attendees Modal */}
      <AnimatePresence>
        {attendeesModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-[70] p-4" 
            onClick={() => setAttendeesModal(null)}
            data-testid="attendees-modal"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()} 
              className="rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden border border-white/[0.1] backdrop-blur-xl flex flex-col"
              style={{ background: 'linear-gradient(145deg, rgba(12,18,32,0.98) 0%, rgba(8,12,22,0.99) 100%)' }}
            >
              {/* Header */}
              <div className="p-5 border-b border-white/[0.06]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/15 border border-blue-500/25 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-['Jost'] font-semibold text-white/90">Participantes</h3>
                      <p className="text-xs text-white/40">{attendeesModal.eventTitle}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setAttendeesModal(null)} 
                    className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-white/70 transition"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Stats - Liquid Glass */}
                <div className="flex gap-3 mt-4">
                  <div className="flex-1 rounded-2xl p-3 text-center backdrop-blur-xl"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(59,130,246,0.04) 100%)',
                      border: '1px solid rgba(139,92,246,0.15)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
                    }}>
                    <p className="text-2xl font-bold text-white" style={{ textShadow: '0 0 20px rgba(139,92,246,0.5)' }}>{attendeesModal.totalAttendees}</p>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">Confirmados</p>
                  </div>
                  <div className="flex-1 rounded-2xl p-3 text-center backdrop-blur-xl"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.04) 100%)',
                      border: '1px solid rgba(59,130,246,0.15)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
                    }}>
                    <p className="text-2xl font-bold text-white" style={{ textShadow: '0 0 20px rgba(59,130,246,0.5)' }}>{attendeesModal.maxAttendees - attendeesModal.totalAttendees}</p>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">Vagas</p>
                  </div>
                  <div className="flex-1 rounded-2xl p-3 text-center backdrop-blur-xl"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(16,185,129,0.04) 100%)',
                      border: '1px solid rgba(34,197,94,0.2)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
                    }}>
                    <p className="text-2xl font-bold text-green-400" style={{ textShadow: '0 0 20px rgba(34,197,94,0.6)' }}>
                      {attendeesModal.attendees.filter(a => a.payment_status === 'pago' || a.payment_status === 'gratis').length}
                    </p>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">Pagos</p>
                  </div>
                </div>

                {/* Filter Tabs - Liquid Glass */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setAttendeesFilter('all')}
                    className="flex-1 py-2.5 rounded-2xl text-xs font-medium transition-all backdrop-blur-xl"
                    style={attendeesFilter === 'all' ? {
                      background: 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(59,130,246,0.15) 100%)',
                      border: '1px solid rgba(139,92,246,0.3)',
                      boxShadow: '0 0 15px rgba(139,92,246,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
                      color: 'white'
                    } : {
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.5)'
                    }}
                    data-testid="filter-all"
                  >
                    Todos ({attendeesModal.totalAttendees})
                  </button>
                  <button
                    onClick={() => setAttendeesFilter('pago')}
                    className="flex-1 py-2.5 rounded-2xl text-xs font-medium transition-all backdrop-blur-xl"
                    style={attendeesFilter === 'pago' ? {
                      background: 'linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(16,185,129,0.15) 100%)',
                      border: '1px solid rgba(34,197,94,0.3)',
                      boxShadow: '0 0 15px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
                      color: '#4ade80'
                    } : {
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.5)'
                    }}
                    data-testid="filter-pago"
                  >
                    Pagos ({attendeesModal.attendees.filter(a => a.payment_status === 'pago' || a.payment_status === 'gratis').length})
                  </button>
                  <button
                    onClick={() => setAttendeesFilter('pendente')}
                    className="flex-1 py-2.5 rounded-2xl text-xs font-medium transition-all backdrop-blur-xl"
                    style={attendeesFilter === 'pendente' ? {
                      background: 'linear-gradient(135deg, rgba(251,191,36,0.2) 0%, rgba(245,158,11,0.15) 100%)',
                      border: '1px solid rgba(251,191,36,0.3)',
                      boxShadow: '0 0 15px rgba(251,191,36,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
                      color: '#fbbf24'
                    } : {
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.5)'
                    }}
                    data-testid="filter-pendente"
                  >
                    Pendentes ({attendeesModal.attendees.filter(a => a.payment_status === 'pendente').length})
                  </button>
                </div>
              </div>

              {/* Attendees List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {attendeesLoading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                ) : attendeesModal.attendees.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-white/[0.03] rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zM12.75 12h-1.5m-6 3h13.5m-13.5 3h13.5" />
                      </svg>
                    </div>
                    <p className="text-white/40 text-sm">Nenhum participante confirmado</p>
                  </div>
                ) : (
                  attendeesModal.attendees
                    .filter(attendee => {
                      if (attendeesFilter === 'all') return true;
                      if (attendeesFilter === 'pago') return attendee.payment_status === 'pago' || attendee.payment_status === 'gratis';
                      if (attendeesFilter === 'pendente') return attendee.payment_status === 'pendente';
                      return true;
                    })
                    .map((attendee, idx) => {
                    const statusBadge = getPaymentStatusBadge(attendee.payment_status);
                    const catBadge = getCategoryBadge(attendee.category);
                    return (
                      <div 
                        key={attendee.member_id}
                        className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.04] transition"
                        data-testid={`attendee-row-${attendee.member_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-lg flex items-center justify-center text-blue-400 text-sm font-bold">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="text-white/90 text-sm font-medium">{attendee.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {attendee.category && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${catBadge.bg} ${catBadge.color}`}>
                                    {catBadge.text}
                                  </span>
                                )}
                                {attendee.phone && (
                                  <span className="text-[10px] text-white/30">{attendee.phone}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${statusBadge.bg} border ${statusBadge.border} ${statusBadge.color}`}>
                            {statusBadge.text}
                          </div>
                        </div>
                        {attendee.payment_status === 'pago' && attendee.amount > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/[0.04] flex items-center justify-between text-[10px]">
                            <span className="text-white/30">Valor pago</span>
                            <span className="text-green-400 font-medium">R$ {attendee.amount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer - Liquid Glass */}
              <div className="p-4 border-t border-white/[0.06] space-y-2">
                <button 
                  onClick={() => {
                    const checkInUrl = `${window.location.origin}/checkin/${attendeesModal.eventId}?key=${attendeesModal.checkinKey || ''}`;
                    navigator.clipboard.writeText(checkInUrl);
                    showNotifMsg('Link de check-in copiado!', 'success');
                  }}
                  className="w-full py-3.5 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                  style={{
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.1) 100%)',
                    border: '1px solid rgba(34,197,94,0.25)',
                    boxShadow: '0 0 20px rgba(34,197,94,0.15), inset 0 1px 0 rgba(255,255,255,0.08)',
                    color: '#4ade80'
                  }}
                  data-testid="copy-checkin-link"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                  </svg>
                  Copiar Link para Hostess
                </button>
                <button 
                  onClick={() => setAttendeesModal(null)}
                  className="w-full py-3.5 rounded-2xl text-sm font-medium backdrop-blur-xl transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.6)'
                  }}
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={!!scannerModal}
        onClose={() => {
          setScannerModal(null);
          setCheckInStats(null);
        }}
        onScan={handleCheckIn}
        eventId={scannerModal?.eventId}
        eventTitle={scannerModal?.eventTitle}
      />

      {/* Promo Image Modal */}
      <AnimatePresence>
        {promoImageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => { setPromoImageModal(null); setShowPromptEditor(false); setCustomPrompt(''); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-lg w-full rounded-3xl overflow-hidden my-4"
              style={{
                background: 'linear-gradient(180deg, rgba(15,15,20,0.98) 0%, rgba(5,5,10,0.99) 100%)',
                border: '1px solid rgba(139,92,246,0.2)'
              }}
            >
              {/* Image with Event Overlay */}
              <div className="relative">
                <img 
                  src={`data:image/png;base64,${promoImageModal.imageBase64}`} 
                  alt={promoImageModal.title}
                  className="w-full h-auto"
                />
                
                {/* Top Overlay - Logo */}
                <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 via-black/40 to-transparent">
                  <div className="flex items-center gap-2">
                    <img src="/w-logo-chrome.png" alt="W" className="w-10 h-10 object-contain" style={{ filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.8))' }} />
                    <span className="text-white font-['Jost'] text-base font-bold tracking-wider" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>WCLUB</span>
                  </div>
                </div>
                
                {/* Bottom Overlay - Event Details (Editable) */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/70 to-transparent">
                  <h3 className="text-xl font-['Jost'] font-bold text-white uppercase tracking-wide mb-2" 
                    style={{ textShadow: '0 2px 15px rgba(0,0,0,0.9)' }}>
                    {overlayData.title || promoImageModal.title}
                  </h3>
                  
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {/* Date */}
                    {overlayData.date && (
                      <div className="flex items-center gap-1.5 text-white/90" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.8)' }}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                        <span>{overlayData.date}</span>
                      </div>
                    )}
                    
                    {/* Location */}
                    {overlayData.location && (
                      <div className="flex items-center gap-1.5 text-white/90" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.8)' }}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                        </svg>
                        <span>{overlayData.location}</span>
                      </div>
                    )}
                    
                    {/* Vagas */}
                    {overlayData.vagas && (
                      <div className="flex items-center gap-1.5 text-white/90" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.8)' }}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                        </svg>
                        <span>{overlayData.vagas}</span>
                      </div>
                    )}
                    
                    {/* Price */}
                    <div className="ml-auto px-3 py-1 rounded-full font-semibold"
                      style={{ 
                        background: overlayData.price !== 'FREE' ? 'rgba(139,92,246,0.3)' : 'rgba(34,197,94,0.3)',
                        color: overlayData.price !== 'FREE' ? '#c4b5fd' : '#86efac',
                        textShadow: '0 1px 5px rgba(0,0,0,0.5)'
                      }}>
                      {overlayData.price || 'FREE'}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Overlay Editor - Edit text that appears on image */}
              <AnimatePresence>
                {showOverlayEditor && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 border-b border-white/[0.06] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Editar Texto da Imagem</span>
                        <button onClick={() => setShowOverlayEditor(false)} className="text-white/40 hover:text-white/60">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <input
                        type="text"
                        value={overlayData.title}
                        onChange={(e) => setOverlayData({ ...overlayData, title: e.target.value })}
                        placeholder="Título do Evento"
                        className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none text-sm"
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={overlayData.date}
                          onChange={(e) => setOverlayData({ ...overlayData, date: e.target.value })}
                          placeholder="Data (ex: 15/06/26)"
                          className="px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none text-sm"
                        />
                        <input
                          type="text"
                          value={overlayData.location}
                          onChange={(e) => setOverlayData({ ...overlayData, location: e.target.value })}
                          placeholder="Local"
                          className="px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none text-sm"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={overlayData.price}
                          onChange={(e) => setOverlayData({ ...overlayData, price: e.target.value })}
                          placeholder="Valor (ex: R$ 150,00 ou FREE)"
                          className="px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none text-sm"
                        />
                        <input
                          type="text"
                          value={overlayData.vagas}
                          onChange={(e) => setOverlayData({ ...overlayData, vagas: e.target.value })}
                          placeholder="Vagas (ex: 20 vagas)"
                          className="px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none text-sm"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Prompt Editor */}
              <AnimatePresence>
                {showPromptEditor && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 border-b border-white/[0.06] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Prompt Personalizado</span>
                        <button onClick={() => setShowPromptEditor(false)} className="text-white/40 hover:text-white/60">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Ex: Charuto cubano premium com café expresso em porcelana fina, ambiente sofisticado com lareira..."
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none text-sm resize-none"
                        rows={3}
                      />
                      <button
                        onClick={() => {
                          const event = promoImageModal.event;
                          if (event) {
                            setPromoImageModal(null);
                            setEvents(prev => prev.map(e => e.id === event.id ? { ...e, promo_image: null } : e));
                            setTimeout(() => handleGenerateImage({ ...event, promo_image: null }, customPrompt || null), 100);
                          }
                        }}
                        disabled={generatingImage}
                        className="w-full py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                        style={{
                          background: 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(59,130,246,0.15) 100%)',
                          border: '1px solid rgba(139,92,246,0.3)',
                          color: '#c4b5fd'
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        </svg>
                        Gerar com Prompt
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Logo Editor */}
              <AnimatePresence>
                {showLogoEditor && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 border-b border-white/[0.06] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Configurar Logo</span>
                        <button onClick={() => setShowLogoEditor(false)} className="text-white/40 hover:text-white/60">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Position - only top-left with vertical adjustment */}
                      <div className="space-y-2">
                        <span className="text-[10px] text-white/40 uppercase">Posição Vertical do Logo</span>
                        <input
                          type="range"
                          min="30"
                          max="150"
                          value={logoSettings.verticalOffset || 50}
                          onChange={(e) => setLogoSettings(prev => ({ ...prev, position: 'top-left', verticalOffset: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                        <div className="flex justify-between text-[9px] text-white/30">
                          <span>Mais alto</span>
                          <span>Mais baixo</span>
                        </div>
                      </div>
                      
                      {/* Size slider */}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-[10px] text-white/40 uppercase">Tamanho</span>
                          <span className="text-[10px] text-white/60">{logoSettings.size}px</span>
                        </div>
                        <input
                          type="range"
                          min="150"
                          max="300"
                          value={logoSettings.size}
                          onChange={(e) => setLogoSettings(prev => ({ ...prev, size: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                      </div>
                      
                      {/* Custom logo upload */}
                      <div className="flex gap-2">
                        <label className="flex-1 py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-2 cursor-pointer transition-all"
                          style={{
                            background: logoSettings.customLogo 
                              ? 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(168,85,247,0.1) 0%, rgba(139,92,246,0.08) 100%)',
                            border: logoSettings.customLogo 
                              ? '1px solid rgba(34,197,94,0.25)'
                              : '1px solid rgba(168,85,247,0.2)',
                            color: logoSettings.customLogo ? '#4ade80' : '#c084fc'
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                          </svg>
                          {logoSettings.customLogo ? 'Logo Anexado' : 'Anexar Logo'}
                          <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                        </label>
                        {logoSettings.customLogo && (
                          <button
                            onClick={() => setLogoSettings(prev => ({ ...prev, customLogo: null }))}
                            className="py-2.5 px-3 rounded-xl text-xs transition-all"
                            style={{
                              background: 'rgba(239,68,68,0.1)',
                              border: '1px solid rgba(239,68,68,0.2)',
                              color: '#f87171'
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Actions */}
              <div className="p-4 space-y-3">
                {/* Upload Photo Section - Admin only */}
                {isAdmin && (
                  <div className="flex gap-2">
                    <label className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all cursor-pointer"
                      style={{
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(37,99,235,0.08) 100%)',
                        border: '1px solid rgba(59,130,246,0.2)',
                        color: '#60a5fa'
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      Galeria
                      <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                    </label>
                    {uploadedPhoto && (
                      <button
                        onClick={useUploadedPhoto}
                        className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                        style={{
                          background: 'linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(16,185,129,0.15) 100%)',
                          border: '1px solid rgba(34,197,94,0.3)',
                          color: '#4ade80'
                        }}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Usar Foto
                      </button>
                    )}
                  </div>
                )}
                
                {/* Main Actions */}
                <div className="flex gap-2">
                  {/* Share button - everyone */}
                  <button
                    onClick={openShareFormatPicker}
                    className="flex-1 py-3.5 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                    style={{
                      background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.1) 100%)',
                      border: '1px solid rgba(34,197,94,0.25)',
                      boxShadow: '0 0 20px rgba(34,197,94,0.15)',
                      color: '#4ade80'
                    }}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                    </svg>
                    Compartilhar
                  </button>
                  
                  {/* Admin-only buttons */}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => { setShowOverlayEditor(!showOverlayEditor); setShowPromptEditor(false); setShowLogoEditor(false); }}
                        className="py-3.5 px-4 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                        style={{
                          background: showOverlayEditor 
                            ? 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(139,92,246,0.15) 100%)'
                            : 'linear-gradient(135deg, rgba(168,85,247,0.1) 0%, rgba(139,92,246,0.08) 100%)',
                          border: '1px solid rgba(168,85,247,0.2)',
                          color: '#c084fc'
                        }}
                        title="Editar Textos"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                        </svg>
                      </button>
                      <button
                        onClick={() => { setShowLogoEditor(!showLogoEditor); setShowOverlayEditor(false); setShowPromptEditor(false); }}
                        className="py-3.5 px-4 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                        style={{
                          background: showLogoEditor 
                            ? 'linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.15) 100%)'
                            : 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(37,99,235,0.08) 100%)',
                          border: '1px solid rgba(59,130,246,0.2)',
                          color: '#60a5fa'
                        }}
                        title="Configurar Logo"
                      >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => { setShowPromptEditor(!showPromptEditor); setShowOverlayEditor(false); setShowLogoEditor(false); }}
                    className="py-3.5 px-4 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                    style={{
                      background: showPromptEditor 
                        ? 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(59,130,246,0.15) 100%)'
                        : 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(59,130,246,0.08) 100%)',
                      border: '1px solid rgba(139,92,246,0.2)',
                      color: '#a78bfa'
                    }}
                    title="Gerar com IA"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const event = promoImageModal.event;
                      if (event) {
                        setPromoImageModal(null);
                        setShowPromptEditor(false);
                        setShowOverlayEditor(false);
                        setShowLogoEditor(false);
                        setEvents(prev => prev.map(e => e.id === event.id ? { ...e, promo_image: null } : e));
                        setTimeout(() => handleGenerateImage({ ...event, promo_image: null }), 100);
                      }
                    }}
                    className="py-3.5 px-4 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-xl transition-all"
                    style={{
                      background: 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(245,158,11,0.08) 100%)',
                      border: '1px solid rgba(251,191,36,0.2)',
                      color: '#fbbf24'
                    }}
                    title="Gerar Nova"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                  </button>
                    </>
                  )}
                </div>
                
                <button 
                  onClick={() => { setPromoImageModal(null); setShowPromptEditor(false); setShowOverlayEditor(false); setShowLogoEditor(false); setCustomPrompt(''); setUploadedPhoto(null); }}
                  className="w-full py-3 rounded-2xl text-sm font-medium backdrop-blur-xl transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.6)'
                  }}
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Format Picker Modal */}
      <AnimatePresence>
        {shareFormat === 'picker' && promoImageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-end justify-center"
            onClick={() => setShareFormat(null)}
          >
            <motion.div
              initial={{ y: 300, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 300, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-t-3xl overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, rgba(20,20,25,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderBottom: 'none'
              }}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">Formato da Imagem</h3>
                  <button onClick={() => setShareFormat(null)} className="text-white/40 hover:text-white/60">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <p className="text-white/50 text-sm">Escolha o formato ideal para compartilhar</p>
                
                <div className="grid grid-cols-2 gap-4 pt-2">
                  {/* Feed Option */}
                  <button
                    onClick={() => handleShareImage(promoImageModal.imageBase64, overlayData.title || promoImageModal.title, 'feed')}
                    className="p-4 rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.1) 100%)',
                      border: '1px solid rgba(59,130,246,0.25)'
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {/* Feed aspect ratio preview */}
                      <div className="w-10 h-12 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center">
                        <span className="text-[8px] text-white/60">4:5</span>
                      </div>
                      <div>
                        <span className="text-white font-medium">Feed</span>
                        <p className="text-white/40 text-xs">1080 × 1350</p>
                      </div>
                    </div>
                    <p className="text-white/50 text-xs">Ideal para posts no Instagram e Facebook</p>
                  </button>
                  
                  {/* Stories Option */}
                  <button
                    onClick={() => handleShareImage(promoImageModal.imageBase64, overlayData.title || promoImageModal.title, 'stories')}
                    className="p-4 rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(139,92,246,0.1) 100%)',
                      border: '1px solid rgba(168,85,247,0.25)'
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {/* Stories aspect ratio preview */}
                      <div className="w-7 h-12 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center">
                        <span className="text-[8px] text-white/60">9:16</span>
                      </div>
                      <div>
                        <span className="text-white font-medium">Stories</span>
                        <p className="text-white/40 text-xs">1080 × 1920</p>
                      </div>
                    </div>
                    <p className="text-white/50 text-xs">Ideal para Stories e Reels</p>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* W Convite Premium Modal */}
      <AnimatePresence>
        {inviteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-end justify-center"
            onClick={() => setInviteModal(null)}>
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-lg max-h-[85vh] rounded-t-3xl overflow-hidden"
              style={{ background: 'linear-gradient(180deg, rgba(15,15,20,0.98) 0%, rgba(5,5,10,0.99) 100%)', border: '1px solid rgba(34,211,238,0.15)' }}
              onClick={(e) => e.stopPropagation()}>
              
              <div className="p-5 border-b border-white/[0.06]">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white font-['Jost'] font-bold text-lg">W Convite Premium</h3>
                  <button onClick={() => setInviteModal(null)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <p className="text-cyan-400/80 text-xs">{inviteModal.title}</p>
                <p className="text-white/30 text-[10px] mt-1">W gera áudio personalizado e envia no Wchat + WhatsApp</p>
              </div>

              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2">
                {inviteMembers.map((member) => {
                  const sent = inviteSent.includes(member.id);
                  const sending = inviteSending === member.id;
                  return (
                    <div key={member.id} className="flex items-center justify-between py-3 px-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium truncate">{member.social_name || member.name}</p>
                        <p className="text-white/30 text-[10px]">{member.phone || 'Sem telefone'}</p>
                      </div>
                      {sent ? (
                        <div className="flex items-center gap-1.5 text-emerald-400">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" /></svg>
                          <span className="text-[10px]">Enviado</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleSendInvite(member)}
                          disabled={sending}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-medium transition-all"
                          style={{
                            background: sending ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(6,182,212,0.1) 100%)',
                            border: '1px solid rgba(34,211,238,0.25)',
                            color: '#22d3ee'
                          }}
                        >
                          {sending ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          ) : (
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" /></svg>
                          )}
                          {sending ? 'Gerando...' : 'Convidar'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {inviteSent.length > 0 && (
                <div className="p-4 border-t border-white/[0.06] text-center">
                  <p className="text-emerald-400/80 text-xs">{inviteSent.length} convite(s) enviado(s) com sucesso</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventsSection;
